import { List, Map } from 'immutable';
import { call, cancelled, put, select } from 'redux-saga/effects';
import { SEARCH_DEFAULT_MAX_RESULT } from 'domain/documents/constants';
import { DocumentsFactory } from 'domain/documents/factories';
import Api, { doLogout } from 'domain/api';
import { queryToSearch } from 'domain/router/helpers';
import { documentsAdapter, documentsWithUniqLinkedUngrouped } from 'domain/documents/helpers';
import { parseSearchAggregations } from 'domain/documents/searchAggregations';
import { documentsKeys } from 'domain/documents/queries/keys';
import { queryClient } from 'services/query';
import { isGridWorkSpaceSelector } from 'domain/companies';
import { matchRoute } from 'domain/router/utils';
import { gridDocumentsListAdapter } from 'domain/documents/adapters';
import * as Router from 'domain/router';
import * as actions from 'domain/documents/documentsActions';
import * as selectors from 'domain/documents/documentSelector';
import * as AgGridActions from 'domain/agGrid';

export function* ensureSearchDocuments(
  { params, force, infiniteScroll },
  maxResults = SEARCH_DEFAULT_MAX_RESULT,
  manual = true,
) {
  // query can be passed as param  in case of document next/previous
  // navigation. The reason is that we dont have query in router selector
  // on document page
  const query = params.query ? params.query : yield select(Router.querySelector);

  const { companyType, companyId } = params;
  const workspace = companyType ? { companyType } : {};
  const { category } = params;
  if (manual) {
    yield put({ type: actions.documentSearchAction.request });
  }
  try {
    let count = 0;
    let { pageToken } = params;
    let adoptedData;
    let collectedData = DocumentsFactory();
    let aggregations; // page-1 only; folder pagination pages 2+ send tabs:null — keep the first
    do {
      const { data } = yield call(Api.documentSearchByCategory, {
        params: {
          maxResults: maxResults || SEARCH_DEFAULT_MAX_RESULT,
          category,
          companyId,
          pageToken,
          ...query,
          ...(params.subtype ? { subtags: [params.subtype] } : {}),
          isConfidential: workspace && workspace.companyType === 'confidential',
        },
        paramsSerializer: (d) => queryToSearch(d),
      });
      aggregations = aggregations ?? parseSearchAggregations(data);
      // eslint-disable-next-line prefer-destructuring
      pageToken = data.pageToken;
      adoptedData = documentsAdapter({ ...data });
      collectedData = collectedData
        // eslint-disable-next-line no-loop-func
        .update('list', (l) => l.merge(adoptedData.list))
        .set('pageToken', adoptedData.pageToken)
        .set('count', adoptedData.count)
        .set('company_total', adoptedData.company_total)
        .set('processing', adoptedData.processing)
        .set('count_without_link_panels', adoptedData.count_without_link_panels)
        .set('searchQuery', params.searchQuery);
      count = documentsWithUniqLinkedUngrouped(collectedData.list.toList()).size;
    } while (count < 50 && pageToken != null);
    const type = force ? actions.documentForceSearch.success : actions.documentSearchAction.success;
    yield put({
      type,
      payload: { data: collectedData, infiniteScroll },
      aggregations,
    });
  } catch (err) {
    yield doLogout(actions.documentSearchAction, err);
  } finally {
    if (yield cancelled()) {
      yield put({
        type: actions.documentSearchAction.failure,
        error: 'canceled',
      });
    }
  }
}

// Invalidate the documents/aggregations summary (doctype tabs + sub-type chips + active-category
// count) so it refetches in lock-step with an immediate grid data change. Called only from the
// grid-mutation choke-points below (all already gated on grid view); the query is disabled in tile.
export const invalidateWorkspaceAggregations = () =>
  queryClient.invalidateQueries({ queryKey: documentsKeys.aggregations() });

export function* ensureWorkspaceGridRefreshServerSide(purge) {
  const isGrid = yield select(isGridWorkSpaceSelector);
  const { location } = yield select(Router.routing);
  const isCompanyPageRoute = matchRoute.isCompanyWorkspace(location.pathname);

  // we use same sagas on company and document pages via MoreButton or ContextMenu
  // but refresh data needed only when we stay on company page with grid mode view
  if (isGrid && isCompanyPageRoute) {
    yield put({
      type: AgGridActions.agGridRefreshServerSideAction.type,
      payload: purge,
    });
    invalidateWorkspaceAggregations();
  }
}

export function* ensureWorkspaceGridApplyTransaction(payload) {
  const isGrid = yield select(isGridWorkSpaceSelector);
  const { location } = yield select(Router.routing);
  const isCompanyPageRoute = matchRoute.isCompanyWorkspace(location.pathname);

  // must be run if user is on grid page only, otherwise transaction will be cached in redux and invoked
  // when user returns to grid and grid is not yet initialized.
  // in this case grid will have fetched fresh data so changes in transaction
  // are not applicable
  if (isGrid && isCompanyPageRoute) {
    const { transactionType, nodes } = payload;

    // !!!!IMPORTANT
    // for 'update' transaction type - invoke ensureWorkspaceGridApplyTransaction after dispatch main action to redux
    // - we get actual/merged documents in ensureWorkspaceGridApplyTransaction for transaction
    if (transactionType === 'update') {
      const currentDocumentList = yield select(selectors.documentsByIdSelector);
      const nodesForApplyTransaction = currentDocumentList.filter(({ documentID }) => nodes.includes(documentID));

      yield put({
        type: actions.documentsGridApplyTransactionAction.type,
        payload: {
          transactionType,
          nodes: nodesForApplyTransaction,
        },
      });
    } else if (transactionType === 'remove') {
      const nodesForApplyTransaction = new List(nodes);

      yield put({
        type: actions.documentsGridApplyTransactionAction.type,
        payload: {
          transactionType,
          nodes: nodesForApplyTransaction,
        },
      });
      invalidateWorkspaceAggregations();
    }
  }
}

export function* ensureFetchGridDocumentsForDocumentNavigation({ params, fetchPrevious = false, pageIndex }) {
  const { companyType, companyId, query, category, startRow, endRow } = params;
  const filterModel = yield select(selectors.gridCurrentFiltersSelector);
  const sortModel = yield select(selectors.gridCurrentSortSelector);
  const searchAfterMap = yield select(selectors.getGridSearchAfterByPageIndexSelector);
  const searchAfterKey = String(pageIndex - 1);

  try {
    const {
      data: { items, count, timeStamp, searchAfter },
    } = yield call(Api.getWorkspaceGridRows, {
      data: {
        isConfidential: companyType === 'confidential',
        companyId,
        category,
        startRow,
        endRow,
        searchAfter: pageIndex > 0 ? searchAfterMap.get(searchAfterKey) : null,
        filterModel: filterModel || {},
        sortModel: sortModel || [],
        ...query,
      },
    });

    const fetchedList = gridDocumentsListAdapter(items);
    const currentList = yield select(selectors.documentsByIdSelector);
    const list = fetchPrevious ? fetchedList.merge(currentList) : currentList.merge(fetchedList);

    yield put({
      type: actions.documentsGridGetRowsAction.success,
      payload: {
        list,
        count,
        timeStamp,
        count_without_link_panels: count,
      },
    });

    yield put({
      type: actions.documentsGridSearchAfterByPageIndexAction.type,
      payload: {
        data: Map().set(pageIndex.toString(), List(searchAfter)),
        purge: false,
      },
    });

    yield put({
      type: actions.documentsGridDocumentsByPageIndexAction.type,
      payload: {
        data: Map().set(pageIndex.toString(), fetchedList.keySeq().toList()),
        purge: false,
      },
    });
  } catch (err) {
    yield doLogout(actions.documentsGridGetRowsAction, err);
  } finally {
    if (yield cancelled()) {
      yield put({
        type: actions.documentsGridGetRowsAction.failure,
        error: 'canceled',
      });
    }
  }
}
